import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import client from "shared/utils/axios-config";
import { useMemo } from "react";
import { DEFAULT_DATE_SUBMIT_FORMAT, tzDate } from "shared/utils/dayjs";
import { Notification } from "shared/types";

/* eslint-disable react-hooks/rules-of-hooks */

// Filter-pill categories. Mirrors the server's notification `category` param;
// "all" sends no filter.
export type NotificationCategory =
  | "all"
  | "clash"
  | "booking"
  | "waitlist"
  | "cancel"
  | "package"
  | "reward";

// Today's (Asia/Jakarta) counts of customer self-service events for the studio.
export interface TodayActivitySummary {
  bookings: number;
  waitlist: number;
  cancellations: number;
  /** Every notification created today, all categories. */
  total: number;
}

const getUnseenNotificationCount = () => {
  return useQuery({
    queryKey: ["notification"],
    queryFn: async (): Promise<number | undefined> => {
      const res = await client.get(`/v1/notification/unseen`);
      if (res.status === 200 && !res.data?.error) {
        return res.data.data;
      }
    },
  });
};

export interface NotificationScroll {
  data: Notification[];
  metadata: {
    previousCursor?: number;
    nextCursor?: number;
  };
}

const getNotificationScroll = (
  enabled = true,
  category: NotificationCategory = "all",
  // today: only rows created today (the dashboard rail); the drawer reads
  // the full history. Part of the key so the two never share a cache.
  options: { today?: boolean } = {},
) =>
  useInfiniteQuery<NotificationScroll, Error>({
    // Category in the key so each filter caches separately; the mark-read
    // mutations invalidate the ["notifications"] prefix, covering every filter.
    queryKey: ["notifications", category, options.today ? "today" : "all-time"],
    initialPageParam: undefined,
    queryFn: async ({ pageParam }): Promise<NotificationScroll> => {
      const res = await client.get(`/v1/notification/`, {
        params: {
          cursor: pageParam,
          category: category === "all" ? undefined : category,
          today: options.today ? "1" : undefined,
        },
      });
      if (res.status === 200 && !res.data?.error) {
        return res.data.data;
      }
      throw new Error(res.data?.error || "Failed to fetch notifications");
    },
    getNextPageParam: (
      lastPage: NotificationScroll,
      pages: NotificationScroll[],
    ) => lastPage.metadata.nextCursor,
    enabled,
  });

const getTodayActivitySummary = () =>
  useQuery({
    queryKey: ["today-activity"],
    queryFn: async (): Promise<TodayActivitySummary | undefined> => {
      const res = await client.get(`/v1/notification/today-activity`);
      if (res.status === 200 && !res.data?.error) {
        return res.data.data;
      }
    },
  });

const useGetNotificationSections = (queryData?: {
  pages: NotificationScroll[];
}) => {
  return useMemo(() => {
    const notifications = queryData?.pages.flatMap((data) => data.data) ?? [];
    const map = new Map<string, Notification[]>();
    const addedIds = new Set<string>(); // global de-dupe

    for (const notification of notifications) {
      const dateKey = tzDate(notification.created_at).format(
        DEFAULT_DATE_SUBMIT_FORMAT,
      );
      if (addedIds.has(notification.id)) continue;

      if (!map.has(dateKey)) {
        map.set(dateKey, []);
      }
      map.get(dateKey)?.push(notification);
      addedIds.add(notification.id);
    }

    return {
      size: notifications.length,
      map,
    };
  }, [queryData]);
};

export {
  getNotificationScroll,
  getTodayActivitySummary,
  getUnseenNotificationCount,
  useGetNotificationSections,
};
